import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { tpoGenUrls } from 'config/urls';
import { tpoGenKeys } from '../../../utils/tpoGenQueryKeys';
import { sendNotification } from 'utils/helperMethods';
import type { ComparisonReportDetailResponse } from '../../types';

const duplicateComparisonReport = async (
    reportId: string,
    name?: string,
): Promise<ComparisonReportDetailResponse> => {
    let token = '';
    try { token = localStorage.getItem('accessToken') ?? ''; } catch {}
    const response = await axios(tpoGenUrls.duplicateComparisonReport(reportId), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        data: name ? { name } : undefined,
    });
    return response.data;
};

interface DuplicateArgs {
    reportId: string;
    name?: string;
}

interface UseDuplicateComparisonReportOptions {
    onSuccess?: (data: ComparisonReportDetailResponse) => void;
    successMessage?: string;
    errorMessage?: string;
}

const useDuplicateComparisonReportMutation = ({
    onSuccess,
    successMessage = 'Comparison report duplicated',
    errorMessage = 'Failed to duplicate comparison report',
}: UseDuplicateComparisonReportOptions = {}) => {
    const queryClient = useQueryClient();

    const { mutate, isPending } = useMutation({
        mutationFn: ({ reportId, name }: DuplicateArgs) => duplicateComparisonReport(reportId, name),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: tpoGenKeys.comparisonReports() });
            sendNotification('success', successMessage);
            onSuccess?.(data);
        },
        onError: (error: unknown) => {
            const err = error as { response?: { data?: { detail?: unknown; message?: string } }; message?: string };
            const message = err?.response?.data?.detail;
            const resolved = typeof message === 'string' ? message
                : err?.response?.data?.message ?? err?.message ?? errorMessage;
            sendNotification('error', typeof resolved === 'string' ? resolved : errorMessage);
        },
    });

    return { mutate, isPending };
};

export default useDuplicateComparisonReportMutation;
